import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import './AdminApps.css'; // Reusing the same CSS for consistency
import { Link } from 'react-router-dom';

function AdminUsers() {
  const { currentUser, isSuperAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [apps, setApps] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formMode, setFormMode] = useState('create'); // 'create' or 'edit'
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    bio: ''
  });
  const [userApps, setUserApps] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleSearchTerm, setRoleSearchTerm] = useState('');
  const [appSearchTerm, setAppSearchTerm] = useState('');
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [loadingApps, setLoadingApps] = useState(false);
  const [modifyingRoles, setModifyingRoles] = useState({});
  const [modifyingApps, setModifyingApps] = useState({});
  const [selectedRoleFilter, setSelectedRoleFilter] = useState(null);

  // Fetch all users, apps, and roles on component mount
  useEffect(() => {
    if (isSuperAdmin) {
      fetchUsers();
      fetchApps();
      fetchRoles();
    }
  }, [isSuperAdmin]);

  // Fetch all users
  const fetchUsers = async () => {
    try {
      setLoading(true);
      // Using a general endpoint to get all users
      const response = await apiService.get('/users');
      console.log('Users response:', response);
      
      let usersList = [];
      
      if (response && response.success && Array.isArray(response.data)) {
        usersList = response.data;
        setError(null);
      } else if (response && response.users) {
        // Fallback for backward compatibility
        usersList = response.users;
        setError(null);
      } else {
        console.error('Unexpected response format:', response);
        setError('Failed to load users: Unexpected response format');
        setLoading(false);
        return;
      }
      
      // Fetch roles and apps for each user
      try {
        const usersWithRolesAndApps = await Promise.all(
          usersList.map(async (user) => {
            const userData = { ...user, roles: [], apps: [] };
            
            // Fetch roles
            try {
              const rolesResponse = await apiService.get(`/roles/user/${user.id}`);
              console.log(`Roles for user ${user.id}:`, rolesResponse);
              
              if (rolesResponse && rolesResponse.success && Array.isArray(rolesResponse.data)) {
                userData.roles = rolesResponse.data;
              } else if (rolesResponse && rolesResponse.roles) {
                // Format from documentation
                userData.roles = rolesResponse.roles;
              }
            } catch (err) {
              console.error(`Failed to fetch roles for user ${user.id}:`, err);
            }
            
            // Fetch apps
            try {
              const appsResponse = await apiService.get(`/users/${user.id}/apps`);
              console.log(`Apps for user ${user.id}:`, appsResponse);
              
              if (appsResponse && appsResponse.success && Array.isArray(appsResponse.data)) {
                userData.apps = appsResponse.data;
              } else if (appsResponse && appsResponse.data) {
                // Fallback for backward compatibility
                userData.apps = appsResponse.data;
              }
            } catch (err) {
              console.error(`Failed to fetch apps for user ${user.id}:`, err);
            }
            
            return userData;
          })
        );
        
        setUsers(usersWithRolesAndApps);
      } catch (err) {
        console.error('Failed to fetch roles and apps for users:', err);
        setUsers(usersList.map(user => ({ ...user, roles: [], apps: [] })));
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setError(`Failed to load users: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Fetch users by role
  const fetchUsersByRole = async (roleId) => {
    try {
      setLoading(true);
      // Using the endpoint documented in admin-user-management.md
      const response = await apiService.get(`/roles/${roleId}/users`);
      console.log(`Users with role ${roleId} response:`, response);
      
      if (response && response.success && Array.isArray(response.data)) {
        // New API format
        setUsers(response.data);
        setError(null);
      } else if (response && response.users) {
        // Format from documentation: { message: "...", role: {...}, users: [...] }
        setUsers(response.users);
        setError(null);
      } else {
        console.error('Unexpected users by role response format:', response);
        setError('Failed to load users by role: Unexpected response format');
      }
    } catch (err) {
      console.error('Failed to fetch users by role:', err);
      setError(`Failed to load users by role: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all apps
  const fetchApps = async () => {
    try {
      const response = await apiService.get('/apps');
      console.log('Apps response in AdminUsers:', response);
      
      if (response && response.success && Array.isArray(response.data)) {
        setApps(response.data);
      } else if (response && response.apps) {
        // Fallback for backward compatibility
        setApps(response.apps);
      } else {
        console.error('Unexpected apps response format:', response);
      }
    } catch (err) {
      console.error('Failed to fetch apps:', err);
      // Don't set error state here to avoid disrupting the main user management flow
    }
  };

  // Fetch all roles
  const fetchRoles = async () => {
    try {
      const response = await apiService.get('/roles');
      console.log('Roles response:', response);
      
      if (response && response.success && Array.isArray(response.data)) {
        // New API format
        setRoles(response.data);
      } else if (response && response.roles) {
        // Format from documentation
        setRoles(response.roles);
      } else if (response && Array.isArray(response)) {
        // Simple array format
        setRoles(response);
      } else {
        console.error('Unexpected roles response format:', response);
      }
    } catch (err) {
      console.error('Failed to fetch roles:', err);
      // Don't set error state here to avoid disrupting the main user management flow
    }
  };

  // Fetch user's apps
  const fetchUserApps = async (userId) => {
    try {
      setLoadingApps(true);
      // Using the endpoint documented in admin-user-management.md
      const response = await apiService.get(`/users/${userId}/apps`);
      console.log(`User apps response for user ${userId}:`, response);
      
      if (response && response.success && Array.isArray(response.data)) {
        setUserApps(response.data);
      } else if (response && response.data) {
        // Fallback for backward compatibility
        setUserApps(response.data);
      } else {
        console.error('Unexpected user apps response format:', response);
        setUserApps([]);
      }
    } catch (err) {
      console.error('Failed to fetch user apps:', err);
      setUserApps([]);
    } finally {
      setLoadingApps(false);
    }
  };

  // Fetch user's roles
  const fetchUserRoles = async (userId) => {
    try {
      setLoadingRoles(true);
      // Using the endpoint documented in admin-user-management.md
      const response = await apiService.get(`/roles/user/${userId}`);
      console.log(`User roles response for user ${userId}:`, response);
      
      if (response && response.success && Array.isArray(response.data)) {
        // New API format
        setUserRoles(response.data);
      } else if (response && response.roles) {
        // Format from documentation: { message: "...", roles: [...] }
        setUserRoles(response.roles);
      } else if (response && response.message && Array.isArray(response.data)) {
        // Another possible format
        setUserRoles(response.data);
      } else {
        console.error('Unexpected user roles response format:', response);
        setUserRoles([]);
      }
    } catch (err) {
      console.error('Failed to fetch user roles:', err);
      setUserRoles([]);
    } finally {
      setLoadingRoles(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Select a user to edit
  const handleSelectUser = async (user) => {
    console.log('Selected user:', user);
    console.log('User roles:', user.roles);
    
    setSelectedUser(user);
    setFormMode('edit');
    setFormData({
      username: user.username,
      email: user.email,
      password: '', // Don't populate password for security
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      bio: user.bio || ''
    });
    
    // Reset search terms and modifying states
    setRoleSearchTerm('');
    setAppSearchTerm('');
    setModifyingRoles({});
    setModifyingApps({});
    
    // Fetch user's apps and roles
    await fetchUserApps(user.id);
    await fetchUserRoles(user.id);
  };

  // Reset form for creating a new user
  const handleNewUser = () => {
    setSelectedUser(null);
    setFormMode('create');
    setFormData({
      username: '',
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      bio: ''
    });
    setUserApps([]);
    setUserRoles([]);
    setRoleSearchTerm('');
    setAppSearchTerm('');
    setModifyingRoles({});
    setModifyingApps({});
    
    // If we're currently filtering by role, reset to show all users
    if (selectedRoleFilter) {
      setSelectedRoleFilter(null);
      fetchUsers();
    }
  };

  // Submit form to create or update a user
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      if (formMode === 'create') {
        // Create new user using the documented endpoint
        const response = await apiService.post('/auth/register', {
          username: formData.username,
          email: formData.email,
          password: formData.password,
          first_name: formData.first_name,
          last_name: formData.last_name
        });
        console.log('Create user response:', response);
        
        if (response && response.success && response.data) {
          setError(null);
          handleNewUser(); // Reset form
          fetchUsers(); // Refresh user list
        } else if (response && response.user) {
          // Fallback for backward compatibility
          setError(null);
          handleNewUser(); // Reset form
          fetchUsers(); // Refresh user list
        } else {
          console.error('Unexpected create user response:', response);
          setError('Failed to create user: Unexpected response format');
        }
      } else {
        // Update existing user using the documented endpoint
        const userData = {
          username: formData.username,
          email: formData.email,
          first_name: formData.first_name,
          last_name: formData.last_name,
          bio: formData.bio
        };
        
        // Only include password if it was provided
        if (formData.password) {
          userData.password = formData.password;
        }
        
        const response = await apiService.put(`/users/${selectedUser.id}`, userData);
        console.log('Update user response:', response);
        
        if (response && response.success && response.data) {
          setError(null);
          fetchUsers(); // Refresh user list
        } else if (response && response.user) {
          // Fallback for backward compatibility
          setError(null);
          fetchUsers(); // Refresh user list
        } else {
          console.error('Unexpected update user response:', response);
          setError('Failed to update user: Unexpected response format');
        }
      }
    } catch (err) {
      console.error('Failed to save user:', err);
      setError(`Failed to save user: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Delete a user
  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedUser.username}?`)) {
      return;
    }
    
    try {
      setLoading(true);
      // Using the documented endpoint for deleting a user
      const response = await apiService.delete(`/users/${selectedUser.id}`);
      console.log('Delete user response:', response);
      
      if (response && response.success) {
        setError(null);
        handleNewUser(); // Reset form
        fetchUsers(); // Refresh user list
      } else if (response && response.message) {
        // Fallback for backward compatibility
        setError(null);
        handleNewUser(); // Reset form
        fetchUsers(); // Refresh user list
      } else {
        console.error('Unexpected delete user response:', response);
        setError('Failed to delete user: Unexpected response format');
      }
    } catch (err) {
      console.error('Failed to delete user:', err);
      setError(`Failed to delete user: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Assign a role to a user
  const handleAssignRole = async (roleId) => {
    if (!selectedUser) return;
    
    try {
      setModifyingRoles({ [roleId]: true });
      // Using the documented endpoint for assigning a role
      const response = await apiService.post('/roles/assign', {
        userId: selectedUser.id,
        roleId: roleId
      });
      console.log('Assign role response:', response);
      
      if (response && response.success) {
        // New API format
        fetchUserRoles(selectedUser.id); // Refresh user roles
        setError(null);
      } else if (response && response.message) {
        // Format from documentation
        fetchUserRoles(selectedUser.id); // Refresh user roles
        setError(null);
      } else {
        console.error('Unexpected assign role response:', response);
        setError('Failed to assign role: Unexpected response format');
      }
    } catch (err) {
      console.error('Failed to assign role:', err);
      setError(`Failed to assign role: ${err.message || 'Unknown error'}`);
    } finally {
      setModifyingRoles({ [roleId]: false });
    }
  };

  // Remove a role from a user
  const handleRemoveRole = async (roleId) => {
    if (!selectedUser) return;
    
    try {
      setModifyingRoles({ [roleId]: true });
      // Using the documented endpoint for removing a role
      const response = await apiService.delete(`/roles/user/${selectedUser.id}/role/${roleId}`);
      console.log('Remove role response:', response);
      
      if (response && response.success) {
        // New API format
        fetchUserRoles(selectedUser.id); // Refresh user roles
        setError(null);
      } else if (response && response.message) {
        // Format from documentation
        fetchUserRoles(selectedUser.id); // Refresh user roles
        setError(null);
      } else {
        console.error('Unexpected remove role response:', response);
        setError('Failed to remove role: Unexpected response format');
      }
    } catch (err) {
      console.error('Failed to remove role:', err);
      setError(`Failed to remove role: ${err.message || 'Unknown error'}`);
    } finally {
      setModifyingRoles({ [roleId]: false });
    }
  };

  // Associate a user with an app
  const handleAddApp = async (appId) => {
    if (!selectedUser) return;
    
    try {
      setModifyingApps({ [appId]: true });
      // Using the documented endpoint for associating a user with an app
      const response = await apiService.post(`/users/${selectedUser.id}/apps`, {
        app_id: appId
      });
      console.log('Add app response:', response);
      
      if (response && response.success) {
        fetchUserApps(selectedUser.id); // Refresh user apps
      } else {
        console.error('Unexpected add app response:', response);
        setError('Failed to add app to user: Unexpected response format');
      }
    } catch (err) {
      console.error('Failed to add app to user:', err);
      setError(`Failed to add app to user: ${err.message || 'Unknown error'}`);
    } finally {
      setModifyingApps({ [appId]: false });
    }
  };

  // Remove a user from an app
  const handleRemoveApp = async (appId) => {
    if (!selectedUser) return;
    
    try {
      setModifyingApps({ [appId]: true });
      // Using the documented endpoint for removing a user from an app
      const response = await apiService.delete(`/users/${selectedUser.id}/apps/${appId}`);
      console.log('Remove app response:', response);
      
      if (response && response.success) {
        fetchUserApps(selectedUser.id); // Refresh user apps
      } else {
        console.error('Unexpected remove app response:', response);
        setError('Failed to remove app from user: Unexpected response format');
      }
    } catch (err) {
      console.error('Failed to remove app from user:', err);
      setError(`Failed to remove app from user: ${err.message || 'Unknown error'}`);
    } finally {
      setModifyingApps({ [appId]: false });
    }
  };

  // Filter users based on search term
  const filteredUsers = users.filter(user => 
    user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.first_name && user.first_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (user.last_name && user.last_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Check if a user has a specific role
  const hasRole = (roleId) => {
    return userRoles.some(role => role.id === roleId);
  };

  // Check if a user is associated with a specific app
  const hasApp = (appId) => {
    return userApps.some(app => {
      // Handle different possible app object structures
      if (app.id === appId) {
        return true;
      }
      if (app.app_id === appId) {
        return true;
      }
      return false;
    });
  };

  // If user is not a superadmin, show access denied message
  if (!isSuperAdmin) {
    return (
      <div className="admin-container">
        <h1>Access Denied</h1>
        <p>You do not have permission to access this page. This area is restricted to superadmins only.</p>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <h1>User Management</h1>
      
      {/* Admin navigation */}
      <div className="admin-nav mb-4">
        <ul className="nav nav-tabs">
          <li className="nav-item">
            <Link to="/admin" className="nav-link">Dashboard</Link>
          </li>
          <li className="nav-item">
            <Link to="/admin/apps" className="nav-link">Apps</Link>
          </li>
          <li className="nav-item">
            <Link to="/admin/users" className="nav-link active">Users</Link>
          </li>
          <li className="nav-item">
            <Link to="/admin/roles" className="nav-link">Roles</Link>
          </li>
        </ul>
      </div>
      
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}
      
      <div className="admin-layout">
        {/* Left side - User list */}
        <div className="admin-sidebar">
          <div className="admin-search">
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-control"
            />
          </div>
          
          <div className="d-flex gap-2 mb-3">
            <button 
              className="btn btn-primary flex-grow-1"
              onClick={handleNewUser}
            >
              Create New User
            </button>
            
            <div className="dropdown">
              <button 
                className="btn btn-outline-secondary dropdown-toggle" 
                type="button" 
                id="filterByRoleDropdown" 
                data-bs-toggle="dropdown" 
                aria-expanded="false"
              >
                {selectedRoleFilter 
                  ? `Role: ${roles.find(r => r.id === selectedRoleFilter)?.name || 'Selected'}` 
                  : 'Filter by Role'}
              </button>
              <ul className="dropdown-menu" aria-labelledby="filterByRoleDropdown">
                <li>
                  <button 
                    className="dropdown-item" 
                    onClick={() => {
                      fetchUsers();
                      setSelectedRoleFilter(null);
                    }}
                  >
                    All Users
                  </button>
                </li>
                {roles.map(role => (
                  <li key={role.id}>
                    <button 
                      className="dropdown-item" 
                      onClick={() => {
                        fetchUsersByRole(role.id);
                        setSelectedRoleFilter(role.id);
                      }}
                    >
                      {role.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          {loading && <p>Loading users...</p>}
          
          <div className="admin-list">
            {filteredUsers.map(user => (
              <div 
                key={user.id} 
                className={`admin-list-item ${selectedUser?.id === user.id ? 'active' : ''}`}
                onClick={() => handleSelectUser(user)}
              >
                <div className="admin-list-item-name">
                  {user.username || user.email}
                  {user.roles && user.roles.length > 0 && (
                    <span className="badge bg-info ms-2">{user.roles.length} role{user.roles.length !== 1 ? 's' : ''}</span>
                  )}
                  {user.apps && user.apps.length > 0 && (
                    <span className="badge bg-secondary ms-2">{user.apps.length} app{user.apps.length !== 1 ? 's' : ''}</span>
                  )}
                </div>
                <div className="admin-list-item-details">
                  {user.email && user.username ? user.email : ''}
                  {user.first_name && user.last_name ? ` - ${user.first_name} ${user.last_name}` : ''}
                </div>
              </div>
            ))}
            
            {filteredUsers.length === 0 && !loading && (
              <p>No users found.</p>
            )}
          </div>
        </div>
        
        {/* Right side - User form */}
        <div className="admin-content">
          <form onSubmit={handleSubmit}>
            <h2>{formMode === 'create' ? 'Create New User' : 'Edit User'}</h2>
            
            <div className="mb-3">
              <label htmlFor="username" className="form-label">Username</label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                className="form-control"
                required
              />
            </div>
            
            <div className="mb-3">
              <label htmlFor="email" className="form-label">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="form-control"
                required
              />
            </div>
            
            <div className="mb-3">
              <label htmlFor="password" className="form-label">
                {formMode === 'create' ? 'Password' : 'Password (leave blank to keep current)'}
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className="form-control"
                required={formMode === 'create'}
              />
            </div>
            
            <div className="mb-3">
              <label htmlFor="first_name" className="form-label">First Name</label>
              <input
                type="text"
                id="first_name"
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                className="form-control"
              />
            </div>
            
            <div className="mb-3">
              <label htmlFor="last_name" className="form-label">Last Name</label>
              <input
                type="text"
                id="last_name"
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
                className="form-control"
              />
            </div>
            
            <div className="mb-3">
              <label htmlFor="bio" className="form-label">Bio</label>
              <textarea
                id="bio"
                name="bio"
                value={formData.bio}
                onChange={handleInputChange}
                className="form-control"
                rows="3"
              ></textarea>
            </div>
            
            <div className="d-flex gap-2 mb-4">
              <button type="submit" className="btn btn-primary">
                {formMode === 'create' ? 'Create User' : 'Update User'}
              </button>
              
              {formMode === 'edit' && (
                <button 
                  type="button" 
                  className="btn btn-danger"
                  onClick={handleDeleteUser}
                >
                  Delete User
                </button>
              )}
              
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={handleNewUser}
              >
                Cancel
              </button>
            </div>
          </form>
          
          {/* Role and App management (only visible when editing a user) */}
          {selectedUser && (
            <div className="mt-4">
              <div className="row">
                {/* Role management */}
                <div className="col-md-6">
                  <h3>Roles</h3>
                  <p className="text-muted mb-2">Users can have multiple roles. Assign or remove roles below.</p>
                  
                  {/* Search filter for roles */}
                  <div className="mb-3">
                    <input
                      type="text"
                      placeholder="Search roles..."
                      value={roleSearchTerm}
                      onChange={(e) => setRoleSearchTerm(e.target.value)}
                      className="form-control"
                    />
                  </div>
                  
                  {loadingRoles ? (
                    <div className="text-center py-3">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading roles...</span>
                      </div>
                      <p className="mt-2">Loading roles...</p>
                    </div>
                  ) : (
                    <>
                      {/* Current roles section */}
                      {userRoles.length > 0 && (
                        <div className="mb-3">
                          <h5>Current Roles</h5>
                          <div className="list-group mb-3">
                            {userRoles.map(role => (
                              <div key={role.id} className="list-group-item d-flex justify-content-between align-items-center">
                                <div>
                                  <strong>{role.name}</strong>
                                  {role.description && <p className="mb-0 small">{role.description}</p>}
                                </div>
                                <button 
                                  className="btn btn-sm btn-danger"
                                  onClick={() => handleRemoveRole(role.id)}
                                  disabled={modifyingRoles[role.id]}
                                >
                                  {modifyingRoles[role.id] ? (
                                    <>
                                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                      Removing...
                                    </>
                                  ) : 'Remove'}
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Available roles section */}
                      <div>
                        <h5>Available Roles</h5>
                        <div className="list-group mb-4">
                          {roles
                            .filter(role => !hasRole(role.id) && 
                              (role.name?.toLowerCase().includes(roleSearchTerm.toLowerCase()) || 
                               role.description?.toLowerCase().includes(roleSearchTerm.toLowerCase())))
                            .map(role => (
                              <div key={role.id} className="list-group-item d-flex justify-content-between align-items-center">
                                <div>
                                  <strong>{role.name}</strong>
                                  {role.description && <p className="mb-0 small">{role.description}</p>}
                                </div>
                                <button 
                                  className="btn btn-sm btn-success"
                                  onClick={() => handleAssignRole(role.id)}
                                  disabled={modifyingRoles[role.id]}
                                >
                                  {modifyingRoles[role.id] ? (
                                    <>
                                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                      Assigning...
                                    </>
                                  ) : 'Assign'}
                                </button>
                              </div>
                            ))}
                          
                          {roles.filter(role => !hasRole(role.id)).length === 0 && (
                            <p className="text-center py-2">No more roles available to assign.</p>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
                
                {/* App management */}
                <div className="col-md-6">
                  <h3>Apps</h3>
                  <p className="text-muted mb-2">Users can be associated with multiple apps. Add or remove apps below.</p>
                  
                  {/* Search filter for apps */}
                  <div className="mb-3">
                    <input
                      type="text"
                      placeholder="Search apps..."
                      value={appSearchTerm}
                      onChange={(e) => setAppSearchTerm(e.target.value)}
                      className="form-control"
                    />
                  </div>
                  
                  {loadingApps ? (
                    <div className="text-center py-3">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading apps...</span>
                      </div>
                      <p className="mt-2">Loading apps...</p>
                    </div>
                  ) : (
                    <>
                      {/* Current apps section */}
                      {userApps.length > 0 && (
                        <div className="mb-3">
                          <h5>Current Apps</h5>
                          <div className="list-group mb-3">
                            {userApps.map(app => {
                              const appId = app.id || app.app_id;
                              const appName = app.name || 'Unknown App';
                              const domain = app.domain || '';
                              
                              return (
                                <div key={appId} className="list-group-item d-flex justify-content-between align-items-center">
                                  <div>
                                    <strong>{appName}</strong>
                                    {domain && <p className="mb-0 small">{domain}</p>}
                                  </div>
                                  <button 
                                    className="btn btn-sm btn-danger"
                                    onClick={() => handleRemoveApp(appId)}
                                    disabled={modifyingApps[appId]}
                                  >
                                    {modifyingApps[appId] ? (
                                      <>
                                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                        Removing...
                                      </>
                                    ) : 'Remove'}
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      
                      {/* Available apps section */}
                      <div>
                        <h5>Available Apps</h5>
                        <div className="list-group">
                          {apps
                            .filter(app => !hasApp(app.id) && 
                              (app.name?.toLowerCase().includes(appSearchTerm.toLowerCase()) || 
                               app.domain?.toLowerCase().includes(appSearchTerm.toLowerCase())))
                            .map(app => (
                              <div key={app.id} className="list-group-item d-flex justify-content-between align-items-center">
                                <div>
                                  <strong>{app.name}</strong>
                                  {app.domain && <p className="mb-0 small">{app.domain}</p>}
                                </div>
                                <button 
                                  className="btn btn-sm btn-success"
                                  onClick={() => handleAddApp(app.id)}
                                  disabled={modifyingApps[app.id]}
                                >
                                  {modifyingApps[app.id] ? (
                                    <>
                                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                      Adding...
                                    </>
                                  ) : 'Add'}
                                </button>
                              </div>
                            ))}
                          
                          {apps.filter(app => !hasApp(app.id)).length === 0 && (
                            <p className="text-center py-2">No more apps available to add.</p>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminUsers; 